import type { AppDispatch, RootState } from "app/store";
import { coordinatesActions, selectCoordinates } from "features/coordinates";
import { measureActions, selectMeasureEntities, selectObjectMeasure } from "features/measure/measureSlice";
import { turnOffObjectMeasureXray } from "features/measure/objectMeasureXray";
import { clippingOutlineLaserActions, selectOutlineLasers } from "features/outlineLaser/outlineLaserSlice";
import { pointLineActions, selectPointLines } from "features/pointLine/pointLineSlice";
import { renderActions, selectClippingPlanes } from "features/render";
import { cloneClippingPlanes } from "features/sectionPlane/sectionPlaneUndoRedo";
import { undoRedoActions } from "features/undoRedo";

/**
 * The reset button while Measure is active: drop the tool's in-progress work and the X-ray the
 * object-to-object tool switched on, and stay in the tool. Not undoable, like the button.
 */
export function resetMeasureTool(dispatch: AppDispatch, state: RootState): void {
    dispatch(coordinatesActions.clear());
    dispatch(clippingOutlineLaserActions.clear());
    dispatch(pointLineActions.clear());
    dispatch(measureActions.clear());
    // The measurement the tool x-rayed the model for is gone, so the x-ray goes with it.
    turnOffObjectMeasureXray(dispatch, state);
}

/**
 * Clear every measurement the reset flyout counts, and put them back on undo.
 * Nothing drawn is a no-op, which is the flyout not offering the button.
 */
export function clearAllMeasurements(dispatch: AppDispatch, state: RootState): void {
    const beforeCoordinates = selectCoordinates(state);
    const beforeLasers = selectOutlineLasers(state);
    const beforePointLines = selectPointLines(state);
    const beforeMeasureEntities = selectMeasureEntities(state).map((set) => structuredClone(set));
    // `measureActions.clear()` drops this too, so undo has to put it back explicitly.
    const beforeObjectMeasure = structuredClone(selectObjectMeasure(state));
    const measurementCount =
        beforeCoordinates.length +
        beforeLasers.length +
        beforePointLines.filter((pointLine) => pointLine.points.length > 0).length +
        beforeMeasureEntities.filter((set) => set.length > 0).length +
        (beforeObjectMeasure.result ? 1 : 0);
    if (measurementCount === 0) {
        return;
    }
    dispatch(coordinatesActions.clear());
    dispatch(clippingOutlineLaserActions.clear());
    dispatch(pointLineActions.clear());
    dispatch(measureActions.clear());
    dispatch(
        undoRedoActions.push({
            description: "Clear measurements",
            undoCallback: () => {
                dispatch(coordinatesActions.setCoordinates(beforeCoordinates));
                dispatch(clippingOutlineLaserActions.setLasers(beforeLasers));
                dispatch(pointLineActions.setPointLines(beforePointLines));
                if (beforeMeasureEntities.length === 1) {
                    dispatch(measureActions.setSelectedEntities(beforeMeasureEntities[0]));
                } else {
                    beforeMeasureEntities.forEach((entities, i) =>
                        dispatch(measureActions.setMeasureSet({ idx: i, entities })),
                    );
                }
                dispatch(measureActions.setObjectMeasure(beforeObjectMeasure));
            },
            redoCallback: () => {
                dispatch(coordinatesActions.clear());
                dispatch(clippingOutlineLaserActions.clear());
                dispatch(pointLineActions.clear());
                dispatch(measureActions.clear());
            },
        }),
    );
}

/** Remove every section plane. Nothing to remove is a no-op, which is the flyout not offering the button. */
export function clearAllSectionPlanes(dispatch: AppDispatch, state: RootState): void {
    const clippingPlanes = selectClippingPlanes(state);
    if (clippingPlanes.planes.length === 0) {
        return;
    }
    const before = { planes: cloneClippingPlanes(clippingPlanes.planes), enabled: clippingPlanes.enabled };
    dispatch(renderActions.setClippingPlanes({ planes: [], enabled: false }));
    dispatch(
        undoRedoActions.push({
            description: "Clear sectioning",
            undoCallback: () => dispatch(renderActions.setClippingPlanes(before)),
            redoCallback: () => dispatch(renderActions.setClippingPlanes({ planes: [], enabled: false })),
        }),
    );
}
